'use client'

import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { Home, ListTodo, Menu, Package, ShoppingBag, User } from 'lucide-react'
import { loginHrefFrom, navigateUrl } from '@/utils/client-request'
import { useCart, isProfileRoute } from '@/lib/commerce/hooks'
import { useScrollVisibility } from '@/hooks/use-scroll-visibility'
import { formatImageURL, isCustomerAuthActive } from '@/utils/util'
import ProfileDrawer from '../profile/ProfileDrawer'

type NavItem = { key: string, icon: any, label: string, isActive: boolean, href?: string, onClick?: () => void, badge?: number | null, isProfile?: boolean }

const itemClass = 'relative flex flex-col items-center justify-center gap-1.5 h-15 min-w-0 transition-colors duration-150 focus-visible:outline-1 focus-visible:-outline-offset-4 focus-visible:outline-foreground'

export default function MobileBottomNav({ vendor, customer, setMobileMenuOpen }: { vendor: any, customer: any, subscription?: any, setMobileMenuOpen: (open: boolean) => void }) {
  const { data: session } = useSession()
  const pathname = usePathname() || ''
  const { cartCount } = useCart()
  const isVisible = useScrollVisibility()
  const [openProfile, setOpenProfile] = useState(false)

  const isLoggedIn = !!session?.user?.id
  const profileActive = isProfileRoute(pathname)

  const items: NavItem[] = [
    { key: 'home', icon: Home, label: 'Нүүр', href: navigateUrl('/', pathname), isActive: !pathname.includes('/product') && !pathname.includes('/cart') && !profileActive && !pathname.includes('/order/check') },
    { key: 'product', icon: Package, label: 'Бараа', href: navigateUrl('/product', pathname), isActive: pathname.includes('/product') && !pathname.includes('/product/') },
    ...(vendor?.canPlaceOrder !== false ? [{ key: 'cart', icon: ShoppingBag, label: 'Сагс', href: navigateUrl('/cart', pathname), isActive: pathname.includes('/cart'), badge: cartCount > 0 ? cartCount : null }] : []),
    isCustomerAuthActive(vendor)
      ? isLoggedIn
        ? { key: 'profile', icon: User, label: 'Профайл', onClick: () => setOpenProfile(true), isActive: profileActive, isProfile: true }
        : { key: 'profile', icon: User, label: 'Нэвтрэх', href: loginHrefFrom(pathname), isActive: pathname.includes('/login') }
      : { key: 'order', icon: ListTodo, label: 'Захиалга', href: navigateUrl('/order/check', pathname), isActive: pathname.includes('/order/check') },
    { key: 'menu', icon: Menu, label: 'Цэс', onClick: () => setMobileMenuOpen(true), isActive: false },
  ]

  const renderInner = (item: NavItem) => {
    const Icon = item.icon
    return (
      <>
        {item.isActive && <span aria-hidden className="absolute top-0 inset-x-5 h-px bg-foreground" />}
        <span className="relative">
          {item.isProfile && session?.user?.avatar ? (
            <img src={formatImageURL(session.user.avatar)} alt="" className="size-5 object-cover bg-muted" />
          ) : (
            <Icon aria-hidden className="size-5" strokeWidth={1.5} />
          )}
          {item.badge && (
            <span className="absolute -top-1.5 -right-3 min-w-4 h-4 px-1 grid place-items-center bg-foreground text-background text-[10px] leading-none tabular-nums">
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          )}
        </span>
        <span className="text-[11px] leading-none tracking-wide">{item.label}</span>
      </>
    )
  }

  return (
    <>
      <nav aria-label="Үндсэн цэс" className={`lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background border-t border-border transition-transform duration-300 motion-reduce:transition-none ${isVisible ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
          {items.map(item => {
            const colorClass = item.isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
            return item.href ? (
              <a key={item.key} href={item.href} aria-current={item.isActive ? 'page' : undefined} className={`${itemClass} ${colorClass}`}>{renderInner(item)}</a>
            ) : (
              <button key={item.key} type="button" onClick={item.onClick} className={`${itemClass} ${colorClass}`}>{renderInner(item)}</button>
            )
          })}
        </div>
      </nav>

      <ProfileDrawer open={openProfile} onClose={() => setOpenProfile(false)} vendor={vendor} customer={customer} isLoading={!customer} setIsLoading={() => {}} />
    </>
  )
}
